import { createBackendApi, type BackendApi } from "../../backend/client"
import { createHttpTransport } from "../../backend/httpTransport"
import { getMyMembershipContext } from "../../commands/memberships"
import { hasCompletedOnboarding, OnSyncMembershipPayload } from "../../reduxToolkit/membershipSlice"
import { AppSupabaseClient, DependentRow, MembershipRoleRow, MembershipRow, OrganizationRow, ProfileRow, SettingsRow, SubscriptionRow } from "../../types/supabase"

export type ReduxStateResult =
  {
    loadedAt: undefined,
    hasCompletedOnboarding: false,
    settings: SettingsRow,
    membership?: undefined
    role?: undefined
    profile?: undefined
    dependents?: undefined
    dependentProfiles?: undefined
    organization?: undefined
    subscription?: undefined
    subscriptionRequired?: undefined
  } | {
    loadedAt: number,
    hasCompletedOnboarding: boolean,
    membership: MembershipRow,
    role: MembershipRoleRow,
    profile: ProfileRow,
    settings: SettingsRow,
    dependents: DependentRow[],
    dependentProfiles: ProfileRow[]
    organization: OrganizationRow | null
    subscription: SubscriptionRow | null
    /**
     * The server's verdict on whether this member's access has lapsed
     * (SubscriptionLimitService::isSubscriptionRequired). Optional because a
     * response from a backend deployed before this field existed won't carry it;
     * consumers fall back to deciding locally -- see isSubscriptionReadOnly.
     */
    subscriptionRequired?: boolean
  }

type MembershipContextResponse =
  {
    has_membership: false
    settings: SettingsRow
  } | {
    has_membership: true
    membership: MembershipRow
    role: MembershipRoleRow
    profile: ProfileRow
    settings: SettingsRow
    dependents: DependentRow[]
    dependent_profiles: ProfileRow[]
    organization: OrganizationRow | null
    subscription: SubscriptionRow | null
    subscription_required?: boolean
  }

/**
 * This query is run when the app is loaded, or when it comes back online,
 * to pull the latest state from the server.
 *
 * Backed by GET /backend/memberships/mine, which runs the equivalent of the
 * ~8 sequential PostgREST reads this used to issue (membership_roles,
 * memberships, profiles, organization_memberships, dependents,
 * subscriptions, settings) server-side, in one request. See
 * docs/plans/native-app-sdk-seams.md, Cleanup B.
 */
export async function loadReduxStateFromServer(
  client: AppSupabaseClient
): Promise<ReduxStateResult> {
  const { data: { session } } = await client.auth.getSession()
  if (!session?.access_token) {
    throw new Error('loadReduxStateFromServer requires an authenticated session')
  }

  const api = createBackendApi(createHttpTransport({
    getAccessToken: async () => (await client.auth.getSession()).data.session?.access_token,
  }))
  return loadReduxStateFromApi(api)
}

/**
 * `loadReduxStateFromServer` for a caller that already holds a backend client --
 * the native app, whose requests need an absolute base URL because a native
 * fetch has no document origin to resolve `/backend` against.
 */
export async function loadReduxStateFromApi(api: BackendApi): Promise<ReduxStateResult> {
  const body = await getMyMembershipContext(api) as MembershipContextResponse

  if (!body.has_membership) {
    return {
      loadedAt: undefined,
      hasCompletedOnboarding: false,
      settings: body.settings
    }
  }

  return {
    loadedAt: Date.now(),
    hasCompletedOnboarding: hasCompletedOnboarding(body.profile),
    membership: body.membership,
    role: body.role,
    profile: body.profile,
    settings: body.settings,
    dependents: body.dependents,
    dependentProfiles: body.dependent_profiles,
    organization: body.organization,
    subscription: body.subscription,
    subscriptionRequired: body.subscription_required
  }
}

/** A `loadReduxStateFromServer` result that actually carried a membership. */
export type LoadedMembershipState = Extract<ReduxStateResult, { loadedAt: number }>

/**
 * Maps a loaded server state onto the `onSyncMembership` payload.
 *
 * This is deliberately the single place that decides which loaded fields reach the
 * store. startupFlow.tsx used to spell the mapping out inline, and when
 * `subscriptionRequired` was added to the loader it was not added to that literal --
 * so the server's expiry verdict was fetched on every startup and then dropped on the
 * floor, leaving `isSubscriptionReadOnly` permanently on its local fallback. Adding a
 * field here now carries it through to every caller.
 */
export function membershipSyncPayload(
  result: LoadedMembershipState,
  userId: string
): OnSyncMembershipPayload {
  return {
    userId,
    membership: result.membership,
    role: result.role,
    profile: result.profile,
    settings: result.settings,
    organization: result.organization,
    subscription: result.subscription,
    subscriptionRequired: result.subscriptionRequired,
  }
}
